import React, { useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { useAllLeaveApi } from './config/useAllLeaveApi';
import { parseTableParams } from '../../utils/Utils';
import { setSignatureStatus } from '../../services/leaveService';
import DataTable from '../../components/ui/DataTable';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import OptionsMenu from '../../components/ui/OptionsMenu';

const Container = styled.div``;

export default function AllLeavesPage() {
  const { t } = useTranslation();
  const history = useHistory();

  const [queryParams, setQueryParams] = useState({ pageSize: 10, pageNumber: 1 });
  const [filterList, setFilterList] = useState([]);
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, ids: [], name: '' });
  const [deleting, setDeleting] = useState(false);

  const { fetching, columns: baseColumns, data, pagination, _delete, reload } = useAllLeaveApi(queryParams);

  const handleSetSignature = useCallback(async (row, next) => {
    try {
      await setSignatureStatus(row.id, next);
      reload?.();
    } catch { /* interceptor */ }
  }, [reload]);

  const handleRowClick = useCallback((row) => {
    if (row?.id) history.push(`/leaves/${row.id}`);
  }, [history]);

  const handleDelete = useCallback((row) => {
    setDeleteConfirm({ isOpen: true, ids: [row.id], name: row.userFullName || '' });
  }, []);

  const handleRowsDelete = useCallback((ids) => {
    setDeleteConfirm({ isOpen: true, ids, name: `${ids.length} ${t('leave.pageTitle').toLowerCase()}` });
  }, [t]);

  const handleDeleteConfirm = useCallback(async () => {
    setDeleting(true);
    await _delete(deleteConfirm.ids);
    setDeleteConfirm({ isOpen: false, ids: [], name: '' });
    setDeleting(false);
  }, [_delete, deleteConfirm.ids]);

  const actionOptions = useMemo(() => [
    { type: 'details', label: t('common.details'), onClick: (row) => history.push(`/leaves/${row.id}`) },
    {
      type: 'markAsSigned',
      label: t('leave.markAsSigned'),
      onClick: (row) => handleSetSignature(row, 'signed'),
      disabled: (row) => row?.signatureStatus === 'signed',
    },
    {
      type: 'markAsUnsigned',
      label: t('leave.markAsUnsigned'),
      onClick: (row) => handleSetSignature(row, 'unsigned'),
      disabled: (row) => row?.signatureStatus !== 'signed',
    },
    { type: 'divider' },
    { type: 'delete', label: t('common.delete'), onClick: handleDelete },
  ], [t, history, handleDelete, handleSetSignature]);

  const columns = useMemo(() => ([
    ...baseColumns,
    {
      name: 'actions', label: ' ',
      options: {
        filter: false, sort: false, download: false, print: false,
        customBodyRender: (_, tableMeta) => <OptionsMenu actions={actionOptions} row={data[tableMeta.rowIndex]} />,
      },
    },
  ]), [baseColumns, actionOptions, data]);

  const handleDataStateChange = useCallback((_, tableState) => {
    setFilterList(tableState.filterList);
    setQueryParams(parseTableParams(tableState, baseColumns));
  }, [baseColumns]);

  return (
    <Container>
      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        title={t('leave.deleteTitle') || t('common.delete')}
        message={t('leave.deleteMessage', { name: deleteConfirm.name }) || `${deleteConfirm.name}?`}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteConfirm({ isOpen: false, ids: [], name: '' })}
        loading={deleting}
      />

      <DataTable
        title={t('leave.allLeaves')}
        fetching={fetching}
        columns={columns}
        data={data}
        totalRowCount={pagination.totalRowCount}
        pageIndex={pagination.pageIndex}
        pageSize={pagination.pageSize}
        onRowClick={handleRowClick}
        onRowsDelete={handleRowsDelete}
        filterList={filterList}
        onTableChanged={handleDataStateChange}
      />
    </Container>
  );
}
